import React, { useEffect, useMemo, useState } from 'react';
import styles from './summary-step.module.css';
import { Anchor, Button, Chip, EditIcon, formatDate, Modal } from '@keplerco/core';
import classNames from 'classnames';
import layoutStyles from '../../../../design/layout.module.css';
import Accordion from '../../../../components/accordion/accordion';
import { EmptyState } from '../../../../components/general/empty-state/empty-state';
import { ReadOnlyField } from '../../../../components/wizard/read-only-field';
import { useKeplerNavigate } from '../../../../navigation/guards/use-kepler-navigate';
import { NotificationPriority, NotificationType } from '../../../../notifications/notifications.models';
import { useAppActions, useAppState } from '../../../../overmind';
import { WidgetCard } from '../../../analytics/analytics-filters/widget-card';
import { SummaryStepRocketIcon } from './summary-step-rocket.icon';
import { ISummaryStepProps } from './summary-step.models';
import { StepIds } from '../rating-assessment.models';
import ratingAssessmentStyles from '../rating-assessment.module.css';
import assessmentsWizardStyles from '../../assessments-wizard.module.css';
import { ConfirmationModalLayout } from '../../../../widgets/layouts';
import { PeerAssessmentAssigneeResponse } from '../../../../overmind/actions';
import { AssessmentStatusChip } from '../../../../components/chips/assessment-status.chip';
import { SkeletonOverlay } from '../../../../components/general/loading-state/skeleton-overlay';
import { RatingAssessmentEmptyStateImage } from '../rating-assessment-empty-state.image';
import { PagePath } from '../../../../navigation/navigation.enums';
import { AssessmentAssigneeType } from '../../../../enums';
import { ScheduleWidget } from '../schedule.widget';
import { FieldValues, useForm } from 'react-hook-form';
import { ExitModal } from '../../../../components/modals/exit.modal';

enum ToastMessage {
  NoSubjects = 'No subjects selected',
  NoAssessors = 'Not all subjects have assessors',
  NoSkills = 'Not all subjects have skills',
}

function SubjectAccordion({ assignee, goToStep }: { assignee: PeerAssessmentAssigneeResponse; goToStep: (stepNumber: number) => void }) {
  return (
    <Accordion
      header={
        <div className={ratingAssessmentStyles.accordionHeader}>
          <div>
            <h5>{assignee.fullName}</h5>
            <div style={{ color: 'var(--text_1)' }}>
              {assignee.department ?? 'No department'} | {assignee.team ?? 'No team'}
            </div>
          </div>

          <div style={{ color: 'var(--text_1)' }}>{assignee.roles?.map(role => role.name).join(', ') ?? 'No job title'}</div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Chip customTheme={{ backgroundColor: 'white', borderColor: 'lilac', color: 'text' }}>
              {assignee.reviewers.length} {assignee.reviewers.length === 1 ? 'assessor' : 'assessors'}
            </Chip>

            <Chip customTheme={{ backgroundColor: 'white', borderColor: 'baby-blue', color: 'text' }}>
              {assignee.companySkills.length} {assignee.companySkills.length === 1 ? 'skill' : 'skills'}
            </Chip>
          </div>
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h6>Assessors</h6>
            <Button fullWidthMobile type="button" square theme="background" onClick={() => goToStep(3)}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <EditIcon tone="text" />
                Edit
              </div>
            </Button>
          </div>

          <div>
            {!!assignee.reviewers.length ? (
              assignee.reviewers.map(reviewer => (
                <div key={reviewer.learnerSlug} className={styles.row}>
                  <div>{reviewer.fullName}</div>

                  <div>
                    {reviewer.isManager && <Chip customTheme={{ backgroundColor: 'white', borderColor: 'lilac', color: 'text' }}>Manager</Chip>}
                    {reviewer.learnerSlug === assignee.learnerSlug && <Chip customTheme={{ backgroundColor: 'white', borderColor: 'baby-blue', color: 'text' }}>Self</Chip>}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState subtitle="No assessors have been added yet" />
            )}
          </div>
        </div>

        <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h6>Skills</h6>
            <Button fullWidthMobile type="button" square theme="background" onClick={() => goToStep(4)}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <EditIcon tone="text" />
                Edit
              </div>
            </Button>
          </div>

          <div>
            {!!assignee.companySkills.length ? (
              assignee.companySkills.map(companySkill => (
                <div key={companySkill.name} className={styles.row} style={{ gridTemplateColumns: '1fr' }}>
                  {companySkill.name}
                </div>
              ))
            ) : (
              <EmptyState subtitle="No skills have been added yet" />
            )}
          </div>
        </div>
      </div>
    </Accordion>
  );
}

export function SummaryStepLayout({ isLoading, setIsLoading, assessment, peerAssessment, goToStep, onBack }: ISummaryStepProps): React.ReactElement {
  const keplerNavigate = useKeplerNavigate();
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const defaultValues: FieldValues = useMemo(
    () => ({
      startDate: assessment?.startDate,
      endDate: assessment?.endDate,
    }),
    [assessment]
  );

  const { control, watch, formState, trigger, setValue } = useForm({ mode: 'onBlur', defaultValues });
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<FieldValues>();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(formState.isDirty);
  const [shouldNavigateAfterActivation, setShouldNavigateAfterActivation] = useState<boolean | null>(null);

  useEffect(() => {
    if (shouldNavigateAfterActivation === null) return;

    keplerNavigate(`${PagePath.assessmentManagementBase}${PagePath.assessmentManagement.replace(':companySlug', companyVariables.slug!)}`);
    setIsOpen(false);
    setIsLoading(false);
    setShouldNavigateAfterActivation(null); 
  }, [shouldNavigateAfterActivation]);

  useEffect(() => {
    if (!fieldValues) setFieldValues(defaultValues);
    watch(setFieldValues);
  }, []);

  useEffect(() => {
    setHasUnsavedChanges(formState.isDirty);
  }, [formState.isDirty]);

  return (
    <React.Fragment>
      <SkeletonOverlay isLoading={isLoading} width="100%">
        <div className={classNames('card', assessmentsWizardStyles.stepContainer)}>
          <div className={styles.header}>
            <SummaryStepRocketIcon />

            <div>
              <h2>Summary</h2>
              <div style={{ color: 'var(--text_1)' }}>Please confirm all the information is correct before activating your assessment</div>
            </div>
          </div>

          <WidgetCard
            title={StepIds.Details}
            button={
              <Button fullWidthMobile type="button" square theme="background" onClick={() => goToStep(1)}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <EditIcon tone="text" />
                  Edit
                </div>
              </Button>
            }
            style={{ marginTop: 120 }}
          >
            <ReadOnlyField name="Name">{assessment.name}</ReadOnlyField>
            <ReadOnlyField name="Description">{assessment.description || 'No description provided'}</ReadOnlyField>
            <ReadOnlyField name="Status">
              <AssessmentStatusChip assessmentStatus={assessment.assessmentStatus} />
            </ReadOnlyField>
          </WidgetCard>

          <WidgetCard
            title={StepIds.Subjects}
            button={
              <Button fullWidthMobile type="button" square theme="background" onClick={() => goToStep(2)}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <EditIcon tone="text" />
                  Edit
                </div>
              </Button>
            }
          >
            {!!peerAssessment.assignees.length ? (
              peerAssessment?.assignees.map(assignee => <SubjectAccordion key={assignee.learnerSlug} assignee={assignee} goToStep={goToStep} />)
            ) : (
              <EmptyState title="" subtitle="The selected subjects will display here" imageElement={<RatingAssessmentEmptyStateImage />} />
            )}
          </WidgetCard>

          <WidgetCard
            title={StepIds.Settings}
            button={
              <Button fullWidthMobile type="button" square theme="background" onClick={() => goToStep(5)}>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <EditIcon tone="text" />
                  Edit
                </div>
              </Button>
            }
          >
            <ReadOnlyField name="Skills feedback">{!!peerAssessment?.isSkillsFeedbackEnabled ? 'Assessors can add comments on the skills that they rate' : ' Skills feedback will not be collected'}</ReadOnlyField>

            <ReadOnlyField name="Assessment feedback">{!!peerAssessment?.isAssessmentFeedbackEnabled ? 'Assessors can capture overall feedback for each subject' : 'Assessment feedback will not be collected'}</ReadOnlyField>

            <ReadOnlyField name="Anonymise feedback">{!!peerAssessment?.isAnonymous ? 'Feedback and scores given will be anonymous' : 'Assessors names will be shown with the scores and feedback they have given'}</ReadOnlyField>

            <ReadOnlyField name="Manager weighting">{!!peerAssessment?.isWeightingEnabled ? 'A weighting is applied to manager ratings' : 'Manager ratings will not be given preference'}</ReadOnlyField>

            <ReadOnlyField name="Completion rules">{!!assessment?.forceAssessment ? 'Participants will be forced to complete the assessment when they access the platform' : 'Participants can complete the assessment when they choose to'}</ReadOnlyField>

            <ReadOnlyField name="Schedule">
              Start: {!!startDate ? formatDate(new Date(startDate)) : 'Immediately'} | End: {!!endDate ? formatDate(new Date(endDate)) : 'Close manually'}
            </ReadOnlyField>
          </WidgetCard>
        </div>
      </SkeletonOverlay>

      <SkeletonOverlay isLoading={isLoading} width="100%">
        <ScheduleWidget
          control={control}
          startDate={startDate}
          endDate={endDate}
          title="When do you want to activate this assessment?"
          onActivationModeChange={isActivateNow => {
            if (isActivateNow) {
              setValue('startDate', undefined);
              setValue('endDate', undefined);
            }
          }}
        />
      </SkeletonOverlay>

      <footer className={classNames('card', layoutStyles.wizardFooter)}>
        <Anchor reverse arrow onClick={onBack}>
          Back
        </Anchor>

        <Button
          filled
          disabled={isLoading}
          type="button"
          arrow={false}
          onClick={async () => {
            const isValid = await trigger();
            if (!isValid) return;

            let message;
            if (!peerAssessment.assignees.length) message = ToastMessage.NoSubjects;
            else if (peerAssessment.assignees.some(assignee => !assignee.reviewers.length)) message = ToastMessage.NoAssessors;
            else if (peerAssessment.assignees.some(assignee => !assignee.companySkills.length)) message = ToastMessage.NoSkills;
            if (!!message) {
              actions.dismissAllNotifications();
              actions.addNotification({
                id: crypto.randomUUID(),
                active: true,
                priority: NotificationPriority.Toast,
                toastIncludeIcon: true,
                type: NotificationType.Error,
                title: 'Your assessment is incomplete',
                message,
              });
              return;
            }

            setIsOpen(true);
          }}
        >
          {startDate || endDate ? 'Schedule activation' : 'Activate'}
        </Button>
      </footer>

      <Modal open={isOpen} onClose={() => setIsOpen(false)}>
        <ConfirmationModalLayout
          title="Ready to launch the assessment"
          subtitle={`This will go live ${startDate ? `on ${formatDate(new Date(startDate))}` : 'immediately'} and ${endDate ? `close on ${formatDate(new Date(endDate))}` : 'be closed manually'}. Are you sure you want to continue?`}
          cancelButtonText="No, take me back"
          onClickCancel={() => setIsOpen(false)}
          submitButtonText="Yes, launch"
          onClickSubmit={async () => {
            setIsLoading(true);

            await actions.updateAssessmentAssignees({
              assessmentSlug: assessment.slug!,
              assigneeType: AssessmentAssigneeType.Learner,
              departmentSlugs: [],
              teamSlugs: [],
              learnerSlugs: peerAssessment.assignees.map(assignee => assignee.learnerSlug),
              includeLearnersWithNoDepartment: true,
            });
            await actions.updateAssessment({
              ...assessment,
              startDate: fieldValues?.startDate || undefined,
              endDate: fieldValues?.endDate || undefined,
            });

            if (!startDate) {
              const response = await actions.activateAssessment(assessment.slug!);

              if (response.error) {
                setIsOpen(false);
                setIsLoading(false);
                return;
              }
            }

            setHasUnsavedChanges(false);
            setShouldNavigateAfterActivation(true);
          }}
        />
      </Modal>

      <ExitModal hasUnsavedChanges={hasUnsavedChanges} />
    </React.Fragment>
  );
}
